'use client'

/**
 * Carries a SERVER-COMPUTED access verdict into PremiumContentGate so its first paint is already
 * correct.
 *
 * 🚨 THE BUG THIS EXISTS FOR (owner, 2026-08-18): "when refreshing live alerts this briefly shows
 * before loading the alerts page" — a paying subscriber saw "Checking access..." on EVERY refresh
 * of the most-visited page on the site, over marketing copy explaining the product they already
 * bought.
 *
 * The gate starts pessimistic (`loading = true`, `hasAccess = null`) and only resolves in an
 * effect, so the first paint was unconditionally the spinner. The 5-minute sessionStorage cache
 * built to avoid exactly this is read INSIDE that effect, so it removed the network wait and not
 * one frame of the flash.
 *
 * ⭐ WHY A SERVER VALUE RATHER THAN SEEDING FROM sessionStorage. Seeding client state from
 * storage during render is a hydration mismatch waiting to happen: the server has no
 * sessionStorage, so it would render the spinner while the client rendered the dashboard, and
 * React would reconcile the difference by complaining. The server already knows the answer —
 * app/layout.tsx resolves the session — so the honest fix is to send the answer, not to guess it
 * twice. Server and client start from the SAME value and there is nothing to reconcile.
 *
 * ⚠ NOT A PAYWALL CHANGE, and that distinction is why this shape was chosen. The value comes from
 * getSessionWithAccessCached(), the same engine the server gates use; it is not the client
 * trusting itself. The gate still revalidates in the background exactly as before.
 */

import { createContext, useContext } from 'react'

export type InitialAccess = {
  hasAccess: boolean
  freeTierAllowUntil: string | null
  freeTierLimitReached: boolean
  /** The server found no session. Lets the gate paint the sign-in card on the FIRST frame instead of a
   *  spinner (register alerts-cls-gate-and-hero-1030). Optional: absent means unknown, the old behaviour. */
  signedIn?: boolean
}

const InitialAccessContext = createContext<InitialAccess | null>(null)

export function InitialAccessProvider({
  value,
  children,
}: {
  value: InitialAccess | null
  children: React.ReactNode
}) {
  return <InitialAccessContext.Provider value={value}>{children}</InitialAccessContext.Provider>
}

/** null when the surface did not provide one — the gate then behaves exactly as it always did. */
export function useInitialAccess(): InitialAccess | null {
  return useContext(InitialAccessContext)
}
